import { useSelector, useDispatch } from 'react-redux';
import { memo, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useDeviceState, useLeave } from '@/libTTS/useCommon';
import { RootState } from '@/store/storeTTS';
import { setVolume, updateScene, updateAIConfig, updateModelMode, setHistoryMsg, updateLocalUser } from '@/store/storeTTS/slices/room';
import utils from '@/utils/utilsTTS/utils';
import style from './index.module.less';
import CameraOpenSVG from '@/assets/img/CameraOpenSVGH5.webp';
import CameraCloseSVG from '@/assets/img/CameraCloseSVGH5.webp';
import Mic from '@/assets/img/mic.svg';
import MicOpenSVG from '@/assets/img/MicOpenSVGH5.webp';
import MicCloseSVG from '@/assets/img/MicCloseSVGH5.webp';
import LeaveRoomSVG from '@/assets/img/leaveBtnH5.webp';
import ScreenOnSVG from '@/assets/img/ScreenOn.webp';
import ScreenOffSVG from '@/assets/img/ScreenOff.webp';

import AudioController from './AudioController';
import { handleCreateBatchTTS } from '@/api/grt/ai';
import { SCENE, Welcome, Model } from '@/configTTS/common';
import { isVisionMode, AI_MODEL } from '@/configTTS/common';
import Config from '@/configTTS';
import RtcClient from '@/libTTS/RtcClient';
import { v4 as uuidv4 } from 'uuid';
import { COMMAND } from '@/utils/utilsTTS/handler';

import VERTC, { MediaType, MirrorType, StreamIndex } from '@volcengine/rtc';
function ToolBar(props: any) {
    const {
        className,
        onBackToChat,
        onDeviceStateChanged,
        ttsMessagesRef,
        currentAgent,
        onCameraToggle,
        TTSNewMsg,
        resetVoiceCallStatus,
        ttsContentRef,
        originMsgMapRef,
        sessionId,
        hideTTSActions,
        setHideTTSActions,
        ...rest
    } = props;
    const room = useSelector((state: RootState) => state.room);
    const model = room.aiConfig.Config.LLMConfig?.ModelName;
    const dispatch = useDispatch();
    const leaveRoom = useLeave();
    const micRef = useRef<HTMLImageElement>(null);
    const device = useSelector((state: RootState) => state.device);
    const [micPermissionDenied, setMicPermissionDenied] = useState(false);
    const [micPermissionError, setMicPermissionError] = useState(false);
    const [cameraPermissionError, setCameraPermissionError] = useState(false);
    const permissionCheckInterval = useRef<NodeJS.Timeout | null>(null);
    // 添加状态来跟踪按钮点击
    const micClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const cameraClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { isAudioPublished, isVideoPublished, isScreenPublished, switchMic, switchCamera, switchScreenCapture } = useDeviceState();

    // 使用真实的音量数据，设置音量阈值（避免环境噪音触发）
    const rawVolume = room.localUser.audioPropertiesInfo?.linearVolume || 0;
    //音量大小
    let volume = { value: rawVolume > 0.05 ? rawVolume : 0 }; // 过滤掉微小的环境噪音
    // 检查是否有麦克风权限
    const hasMicrophonePermission = device.devicePermissions?.audio ?? true;
    // 检查麦克风权限状态
    const checkMicPermission = async () => {
        try {
            // 使用 permissions API 检查麦克风权限
            //@ts-ignore
            if (navigator.permissions && navigator.permissions.query) {
                //@ts-ignore
                const result = await navigator.permissions.query({ name: 'microphone' });
                if (result.state === 'granted') {
                    setMicPermissionError(false);
                    setMicPermissionDenied(false);
                } else if (result.state === 'denied') {
                    setMicPermissionError(true);
                    setMicPermissionDenied(true);
                } else if (result.state === 'prompt') {
                    // 尝试获取媒体流以检查权限
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        stream.getTracks().forEach((track) => track.stop());
                        setMicPermissionError(false);
                        setMicPermissionDenied(false);
                    } catch (err) {
                        setMicPermissionError(true);
                        setMicPermissionDenied(true);
                    }
                }

                // 监听权限状态变化
                result.addEventListener('change', () => {
                    if (result.state === 'granted') {
                        setMicPermissionError(false);
                        setMicPermissionDenied(false);
                    } else if (result.state === 'denied') {
                        setMicPermissionError(true);
                        setMicPermissionDenied(true);
                    }
                });
            } else {
                // 降级处理：直接尝试获取媒体流
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach((track) => track.stop());
                    setMicPermissionError(false);
                    setMicPermissionDenied(false);
                } catch (err) {
                    setMicPermissionError(true);
                    setMicPermissionDenied(true);
                }
            }
        } catch (err) {
            console.log('麦克风权限检查失败:', err);
            setMicPermissionError(true);
            setMicPermissionDenied(true);
        }
    };

    // 检查摄像头权限状态
    const checkCameraPermission = async () => {
        try {
            // 使用 permissions API 检查摄像头权限
            //@ts-ignore
            if (navigator.permissions && navigator.permissions.query) {
                //@ts-ignore
                const result = await navigator.permissions.query({ name: 'camera' });
                if (result.state === 'granted') {
                    setCameraPermissionError(false);
                } else if (result.state === 'denied') {
                    setCameraPermissionError(true);
                } else if (result.state === 'prompt') {
                    // 尝试获取媒体流以检查权限
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                        stream.getTracks().forEach((track) => track.stop());
                        setCameraPermissionError(false);
                    } catch (err) {
                        setCameraPermissionError(true);
                    }
                }

                // 监听权限状态变化
                result.addEventListener('change', () => {
                    if (result.state === 'granted') {
                        setCameraPermissionError(false);
                    } else if (result.state === 'denied') {
                        setCameraPermissionError(true);
                    }
                });
            } else {
                //直接尝试获取媒体流
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach((track) => track.stop());
                    setCameraPermissionError(false);
                } catch (err) {
                    setCameraPermissionError(true);
                }
            }
        } catch (err) {
            console.log('摄像头权限检查失败:', err);
            setCameraPermissionError(true);
        }
    };
    useEffect(() => {
        checkMicPermission();
    }, []);
    // 切换AI场景
    const handleSwitchAIScene = (scene: SCENE) => {
        dispatch(updateScene({ scene }));
        // Config.WelcomeSpeech = Welcome[scene];
    };

    // 切换AI模型到视觉模式
    const switchToVisionModel = () => {
        // 在切换模型前，先清除当前正在进行的AI回答
        if (RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }
        // 更新模型为视觉模型
        Config.Model = AI_MODEL.VISION;
        Config.resetTTSMode();
        dispatch(updateModelMode(Config.ModeSourceType));
        dispatch(updateAIConfig(Config.aigcConfig));
    };

    // 控制麦克风并处理权限拒绝情况
    const handleSwitchMic = async (enable?: boolean) => {
        const enableMic = enable !== undefined ? enable : !isAudioPublished;

        try {
            await switchMic(enableMic);
            // 如果成功切换，重置权限拒绝状态
            setMicPermissionDenied(false);
        } catch (error: any) {
            if (error.message === 'Microphone switch is in progress') {
                return;
            }
            dispatch(
                updateLocalUser({
                    publishAudio: false
                })
            );
            // 设置权限拒绝状态
            setMicPermissionDenied(true);
            console.log('麦克风权限被拒绝');
            throw error;
        }
    };

    // 控制视频
    const handleSwitchCamera = async (enable?: boolean) => {
        checkCameraPermission();
        const enableCamera = enable !== undefined ? enable : !isVideoPublished;

        // 如果存在摄像头权限错误，则阻止操作
        if (cameraPermissionError) {
            // 确保摄像头状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishVideo: false
                })
            );
            return;
        }

        // 在切换前清除历史消息和当前AI状态
        dispatch(setHistoryMsg([]));
        if (RtcClient && RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }
        if (enableCamera) {
            handleSwitchAIScene(SCENE.TEACHING_ASSISTANT);
            if (isScreenPublished) {
                await switchScreenCapture(false);
            }

            // 修复：确保正确处理视频捕获状态
            try {
                // 先停止任何现有的视频捕获
                await RtcClient.stopVideoCapture();
            } catch (e) {
                console.log('stopVideoCapture error:', e);
            }
            // 然后开始新的视频捕获
            try {
                await switchCamera(true);
            } catch (e) {
                console.error('startVideoCapture error:', e);
                // 处理摄像头权限被拒绝的情况，切换回普通模式
                handleSwitchAIScene(SCENE.INTELLIGENT_ASSISTANT);
                Config.Model = Model[SCENE.INTELLIGENT_ASSISTANT];
                dispatch(updateModelMode(Config.ModeSourceType));
                dispatch(updateAIConfig(Config.aigcConfig));

                // 更新本地用户状态
                dispatch(
                    updateLocalUser({
                        publishVideo: false
                    })
                );

                // 更新摄像头权限状态
                setCameraPermissionError(true);
                // 抛出错误以便上层处理
                throw e;
            }

            switchToVisionModel();
            Config.VisionSourceType = StreamIndex.STREAM_INDEX_MAIN;
        } else {
            try {
                await switchCamera(false);
                if (!isScreenPublished) {
                    handleSwitchAIScene(SCENE.INTELLIGENT_ASSISTANT);
                    Config.Model = Model[SCENE.INTELLIGENT_ASSISTANT];
                    dispatch(updateModelMode(Config.ModeSourceType));
                    dispatch(updateAIConfig(Config.aigcConfig));
                }
            } catch (e) {
                console.error('stopVideoCapture error:', e);
                // 即使出错也要确保切换到普通模式
                handleSwitchAIScene(SCENE.INTELLIGENT_ASSISTANT);
                Config.Model = Model[SCENE.INTELLIGENT_ASSISTANT];
                dispatch(updateModelMode(Config.ModeSourceType));
                dispatch(updateAIConfig(Config.aigcConfig));
            }
        }

        if (RtcClient.getAudioBotEnabled()) {
            await RtcClient.updateAudioBot();
        }
        if (onDeviceStateChanged) {
            onDeviceStateChanged('camera');
        }

        // 通知父组件摄像头状态变化（只调用一次）
        onCameraToggle && onCameraToggle();
    };

    useEffect(() => {
        return () => {
            // 语音通话结束事件
            const voiceCallStatusEvent = new CustomEvent('voiceCallStatusChange', {
                detail: { inVoiceCall: false }
            });
            window.dispatchEvent(voiceCallStatusEvent);
        };
    }, []);
    const handleLeaveRoom = async () => {
        if (setHideTTSActions) {
            console.log('hideTTSActions', hideTTSActions);
            setHideTTSActions(true);
        }
        // 从全局变量或localStorage中获取消息列表
        let ttsMessages = TTSNewMsg?.current || [];
        console.log('ttsMessages', ttsMessages);
        if (ttsMessages.length > 0) {
            //    const filteredMessages = ttsMessages.filter((message: any) => {
            //         return message.section_id !== "0";
            //     });
            const filteredMessages = ttsMessages;
            // 创建一个映射来存储相同_id和reply_id的配对
            const idPairs = new Map<string, string>(); // _id -> section_id

            // 第一次遍历：为所有local消息创建section_id映射
            filteredMessages.forEach((message: any) => {
                if (message.role === 'local') {
                    if (!idPairs.has(message._id)) {
                        idPairs.set(message._id, uuidv4());
                    }
                }
            });

            // 第二次遍历：处理assistant消息，确保匹配的local和assistant使用相同section_id
            filteredMessages.forEach((message: any) => {
                if (message.role === 'assistant' && message.reply_id) {
                    // 如果reply_id对应一个local消息，则使用该local消息的section_id
                    if (idPairs.has(message.reply_id)) {
                        idPairs.set(message._id, idPairs.get(message.reply_id)!);
                    } else if (!idPairs.has(message._id)) {
                        // 否则为assistant消息生成新的section_id
                        idPairs.set(message._id, uuidv4());
                    }
                }
            });

            const modifiedMessages = filteredMessages.map((message: any) => {
                console.log('message', message);
                // 根据TTS消息的原始role来映射
                let newRole = 'user';
                if (message.role === 'assistant') {
                    newRole = 'assistant';
                } else if (message.role === 'local') {
                    newRole = 'user';
                }

                // 处理截图消息
                if (message.is_snapshot === true && message.file_json?.file_url) {
                    const fileJsonObj = Array.isArray(message.file_json) && message.file_json.length > 0 ? message.file_json[0] : message.file_json;

                    // 为local角色生成或获取section_id
                    if (message.role === 'local') {
                        return {
                            ...message,
                            role: newRole,
                            content_type: 'text', // 明确指定内容类型
                            file_json: fileJsonObj, // 包含完整的文件元数据
                            section_id: idPairs.get(message._id)
                        };
                    }

                    return {
                        ...message,
                        role: newRole,
                        content_type: 'text', // 明确指定内容类型
                        file_json: fileJsonObj // 包含完整的文件元数据
                    };
                }

                // 为local和assistant角色处理section_id
                if (message.role === 'local' || (message.role === 'assistant' && idPairs.has(message._id))) {
                    return {
                        ...message,
                        role: newRole,
                        section_id: idPairs.get(message._id)
                    };
                } else if (message.role === 'assistant') {
                    // 为assistant角色检查其reply_id是否与某个local的_id匹配
                    if (message.reply_id && idPairs.has(message.reply_id)) {
                        // 使用已匹配的local消息的section_id
                        return {
                            ...message,
                            role: newRole,
                            section_id: idPairs.get(message.reply_id)
                        };
                    } else {
                        // 为没有匹配的assistant消息生成新的section_id
                        const sectionId = uuidv4();
                        return {
                            ...message,
                            role: newRole,
                            section_id: sectionId
                        };
                    }
                }

                return {
                    ...message,
                    role: newRole
                };
            });

            console.log('modifiedMessages========:', modifiedMessages);
            const res = await handleCreateBatchTTS({
                message_list: modifiedMessages
            });

            if (res) {
                console.log('语音通话数据保存成功', res);
            } else {
                console.error('语音通话数据保存失败:', res);
            }
        }
        if (isVideoPublished) {
            switchCamera(false);
        }
        if (isScreenPublished) {
            switchScreenCapture(false);
        }
        // 清理本地引用
        if (ttsMessagesRef.current) {
            ttsMessagesRef.current = [];
        }
        if (ttsContentRef.current) {
            ttsContentRef.current = [];
        }
        if (TTSNewMsg.current) {
            TTSNewMsg.current = [];
        }
        await leaveRoom();
        resetVoiceCallStatus();
        dispatch(updateScene({ scene: SCENE.INTELLIGENT_ASSISTANT })); // 或其他默认场景
        dispatch(setHistoryMsg([]));
        onBackToChat && onBackToChat();
    };
    const isMicEnabled = isAudioPublished && !micPermissionError;
    const isCameraEnabled = isVideoPublished && !cameraPermissionError;

    return (
        <div className={`${className} ${style.btns} ${utils.isMobile() ? style.column : ''}`} {...rest}>
            <div className={`${className} ${style.btnswidth} ${utils.isMobile() ? style.column : ''}`}>
                {/* 摄像头按钮 */}
                <div
                    className={`${style.btn}`}
                    style={{
                        background: isCameraEnabled && !cameraPermissionError ? 'rgba(255,255,255)' : 'rgba(214,223,233,0.6)',
                        opacity: cameraPermissionError ? 1 : 1,
                        pointerEvents: cameraPermissionError ? 'auto' : 'auto'
                    }}
                >
                    <img
                        src={!cameraPermissionError && isCameraEnabled ? CameraOpenSVG : CameraCloseSVG}
                        onClick={() => {
                            // 防止重复点击
                            if (cameraClickTimeoutRef.current) {
                                return;
                            }

                            cameraClickTimeoutRef.current = setTimeout(() => {
                                handleSwitchCamera();
                                if (cameraClickTimeoutRef.current) {
                                    clearTimeout(cameraClickTimeoutRef.current);
                                    cameraClickTimeoutRef.current = null;
                                }
                            }, 1000);
                        }}
                        className={`${style.cameraBtn}`}
                        alt="camera"
                        style={{ opacity: cameraPermissionError ? 1 : 1 }}
                    />
                </div>

                {/* 麦克风按钮 */}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <div className={`${style.btn}`} style={{ background: isMicEnabled ? 'rgba(255,255,255)' : 'rgba(214,223,233,0.6)' }}>
                        <div
                            className={`${style.micBtn}`}
                            onClick={() => {
                                // 防止重复点击
                                if (micClickTimeoutRef.current) {
                                    return;
                                }

                                micClickTimeoutRef.current = setTimeout(() => {
                                    handleSwitchMic(!isMicEnabled);
                                    if (micClickTimeoutRef.current) {
                                        clearTimeout(micClickTimeoutRef.current);
                                        micClickTimeoutRef.current = null;
                                    }
                                }, 1000);
                            }}
                            style={{
                                cursor: 'pointer',
                                lineHeight: '1',
                                position: 'absolute',
                                zIndex: 1
                            }}
                        >
                            {isMicEnabled ? (
                                <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="icon">
                                        <path
                                            id="Union"
                                            d="M32.0027 3.33008C38.2615 3.33008 43.338 8.40344 43.3386 14.6621V31.998C43.3379 38.2566 38.2614 43.3301 32.0027 43.3301C25.7445 43.3294 20.6714 38.2562 20.6707 31.998V14.6621C20.6713 8.40387 25.7445 3.33077 32.0027 3.33008Z"
                                            fill="#141414"
                                        />
                                        <g id="Group 1321321636">
                                            <mask
                                                id="mask0_297_2000"
                                                mask-type="alpha"
                                                maskUnits="userSpaceOnUse"
                                                x="20"
                                                y="3"
                                                width="24"
                                                height="41"
                                            >
                                                <path
                                                    id="Union_2"
                                                    d="M32.0027 3.33008C38.2615 3.33008 43.338 8.40344 43.3386 14.6621V31.998C43.3379 38.2566 38.2614 43.3301 32.0027 43.3301C25.7445 43.3294 20.6714 38.2562 20.6707 31.998V14.6621C20.6713 8.40387 25.7445 3.33077 32.0027 3.33008Z"
                                                    fill="#141414"
                                                />
                                            </mask>
                                            <g mask="url(#mask0_297_2000)">
                                                <rect
                                                    id="Rectangle 34625728"
                                                    x="17.5"
                                                    y="21.5"
                                                    width="29"
                                                    height="21"
                                                    fill="#141414"
                                                    stroke="#141414"
                                                />
                                            </g>
                                        </g>
                                        <path
                                            id="Union_3"
                                            d="M52 28.9199C52.9665 28.9199 53.75 29.7034 53.75 30.6699C53.7499 42.093 44.9439 51.4585 33.75 52.3496V58.6699C33.75 59.6364 32.9665 60.4199 32 60.4199C31.0336 60.4198 30.25 59.6363 30.25 58.6699V52.3486C19.0563 51.4573 10.2501 42.0929 10.25 30.6699C10.25 29.7035 11.0336 28.92 12 28.9199C12.9665 28.9199 13.75 29.7034 13.75 30.6699C13.7501 40.749 21.9209 48.9198 32 48.9199C42.0792 48.9199 50.2499 40.7491 50.25 30.6699C50.25 29.7035 51.0336 28.92 52 28.9199Z"
                                            fill="#141414"
                                        />
                                    </g>
                                </svg>
                            ) : (
                                <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="icon">
                                        <path
                                            id="Subtract"
                                            d="M12 28.9131C12.9664 28.9131 13.7499 29.6967 13.75 30.6631C13.7503 40.742 21.921 48.9131 32 48.9131C35.8654 48.9131 39.4486 47.7095 42.3994 45.6592L44.9082 48.168C41.7376 50.5097 37.909 52.0116 33.75 52.3428V58.6631C33.7498 59.6294 32.9664 60.4131 32 60.4131C31.0336 60.4131 30.2502 59.6294 30.25 58.6631V52.3428C19.0563 51.4515 10.2503 42.0859 10.25 30.6631C10.2501 29.6967 11.0336 28.9131 12 28.9131ZM38.2129 41.4727C36.4287 42.6438 34.2963 43.3281 32.0029 43.3281C25.7447 43.3275 20.6716 38.2543 20.6709 31.9961V23.9307L38.2129 41.4727ZM52 28.9131C52.9664 28.9131 53.7499 29.6967 53.75 30.6631C53.7499 34.6817 52.6581 38.4449 50.7578 41.6748L48.1836 39.1016C49.5023 36.5777 50.2499 33.7078 50.25 30.6631C50.2501 29.6967 51.0336 28.9131 52 28.9131ZM32.0029 3.32812C38.2615 3.32827 43.3369 8.40182 43.3379 14.6602V31.9961C43.3378 32.702 43.2691 33.3923 43.1455 34.0625L21.0059 11.9238C22.2306 6.98797 26.6885 3.32866 32.0029 3.32812Z"
                                            fill="#141414"
                                        />
                                        <path
                                            id="Rectangle 34625722 (Stroke)"
                                            d="M10.547 5.54191L58.5559 53.5508C59.1336 54.1285 59.1336 55.0651 58.5559 55.6428C57.9782 56.2204 57.0416 56.2204 56.4639 55.6428L8.45504 7.63387C7.87736 7.05619 7.87736 6.11959 8.45504 5.54191C9.03272 4.96423 9.96932 4.96423 10.547 5.54191Z"
                                            fill="#141414"
                                        />
                                    </g>
                                </svg>
                            )}
                            {isMicEnabled && (
                                <div className={style.boxBtn3}>
                                    <div
                                        className={style.valueBtn3}
                                        style={{
                                            transform: `translateY(${16 - (volume.value / 100) * 16}px)`,
                                            height: `${(volume.value / 100) * 30}px`
                                        }}
                                    ></div>
                                </div>
                            )}
                        </div>
                        {/* <img
              src={isAudioPublished ? MicOpenSVG : MicCloseSVG}
              onClick={() => switchMic(true)}
              className={`${style.micBtn}`}
              alt="mic"
              ref={micRef}
            /> */}
                    </div>
                </div>

                {/* 挂断按钮 */}
                <div className={`${style.btn}`}>
                    <img src={LeaveRoomSVG} onClick={() => handleLeaveRoom()} className={`${style.leaveBtn}`} alt="leave" />
                </div>
            </div>
        </div>
    );
}
export default memo(ToolBar);
